import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../utils/axios';

// ---------- Types ----------
interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface CompanyConfigResponse {
  companyName: string;
  address: string;
  phone: string;
  mainEmail: string;
  supportEmail: string;
  billingEmail: string;
  contactEmail: string;
  logoBase64: string;
  tpsNumber: string;
  tvqNumber: string;
  neq: string;
  bankName: string;
  bankInstitution: string;
  bankAccount: string;
  bankTransit: string;
  bankAddress: string;
}

interface SystemConfigState {
  config: RequestState<CompanyConfigResponse>;
  update: RequestState<CompanyConfigResponse>;
}

// ---------- Initial State ----------
const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: SystemConfigState = {
  config: initialRequestState<CompanyConfigResponse>(),
  update: initialRequestState<CompanyConfigResponse>(),
};

// ---------- Thunks ----------
const BASE_URL = '/api/v1/company-config';

export const fetchSystemConfig = createAsyncThunk(
  'systemConfig/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(BASE_URL);
      return res.data as CompanyConfigResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch system config failed');
    }
  }
);

export const updateSystemConfig = createAsyncThunk(
  'systemConfig/update',
  async (data: CompanyConfigResponse, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(BASE_URL, data);
      return res.data as CompanyConfigResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update system config failed');
    }
  }
);

// ---------- Slice ----------
const systemConfigSlice = createSlice({
  name: 'systemConfig',
  initialState,
  reducers: {
    clearUpdateState: (state) => {
      state.update = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchSystemConfig.pending, (state) => {
      state.config.loading = true;
      state.config.error = null;
    });
    builder.addCase(fetchSystemConfig.fulfilled, (state, action) => {
      state.config.loading = false;
      state.config.data = action.payload;
    });
    builder.addCase(fetchSystemConfig.rejected, (state, action) => {
      state.config.loading = false;
      state.config.error = action.payload as string;
    });

    // Update
    builder.addCase(updateSystemConfig.pending, (state) => {
      state.update.loading = true;
      state.update.error = null;
    });
    builder.addCase(updateSystemConfig.fulfilled, (state, action) => {
      state.update.loading = false;
      state.update.data = action.payload;
      state.config.data = action.payload; // Update central config
    });
    builder.addCase(updateSystemConfig.rejected, (state, action) => {
      state.update.loading = false;
      state.update.error = action.payload as string;
    });
  },
});

export const { clearUpdateState } = systemConfigSlice.actions;
export default systemConfigSlice.reducer;
